import { useEffect, useRef, useState, Suspense, lazy } from 'react';
import * as THREE from 'three';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

const BG_IMAGE = "https://static.prod-images.emergentagent.com/jobs/5dcdaf2f-71a5-4554-89aa-95e1d120174a/images/4cb0cacc70a24063e3377690ff627fe9c612e7e7d5c48c05c1ad652f04d88eca.png";

const AI_AGENTS = [
  { name: 'Christelle', title: 'Assistante Intelligence', color: 0xFFAD00, img: '/agent-gold.png' },
  { name: 'Rachel', title: 'Finance Intelligence', color: 0x34C759, img: '/agent-green.png' },
  { name: 'Fatima', title: 'Formation Intelligence', color: 0xEC4899, img: '/agent-pink.png' },
  { name: 'Samah', title: 'Téléprospection Intelligence', color: 0x007AFF, img: '/agent-blue.png' },
  { name: 'Farah', title: 'Développement Commercial', color: 0xA855F7, img: '/agent-violet.png' },
];

const SLIDES = [
  { id: 'telepro-journey', label: 'Parcours Automatisé Télépro', component: lazy(() => import('./admin/TeleproAnimatedJourney')) },
  { id: 'workflow', label: 'Workflow SAN-IA Complet', component: lazy(() => import('./admin/WorkflowComplet3D')) },
];

// ═══ Agent helpers ═══
function createGlowTexture(size, color) {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');
  const r = size / 2;
  const g = ctx.createRadialGradient(r, r, 0, r, r, r);
  const hex = '#' + color.toString(16).padStart(6, '0');
  g.addColorStop(0, hex + '40');
  g.addColorStop(0.5, hex + '15');
  g.addColorStop(1, hex + '00');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return new THREE.CanvasTexture(c);
}

function createLabelTexture(name, title, colorHex) {
  const c = document.createElement('canvas');
  c.width = 256; c.height = 64;
  const ctx = c.getContext('2d');
  ctx.clearRect(0, 0, 256, 64);
  ctx.textAlign = 'center';
  ctx.fillStyle = colorHex;
  ctx.font = 'bold 20px system-ui';
  ctx.fillText(name, 128, 22);
  ctx.globalAlpha = 0.5;
  ctx.font = '13px system-ui';
  ctx.fillText(title, 128, 46);
  return new THREE.CanvasTexture(c);
}

// ═══ Loading fallback ═══
function SlideLoader() {
  return (
    <div className="flex items-center justify-center w-full h-full" data-testid="slide-loader">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
        <span className="text-slate-400 text-sm">Chargement de la vue 3D...</span>
      </div>
    </div>
  );
}

// ═══ Main Component ═══
export default function NeuralNetworkHero() {
  const containerRef = useRef(null);
  const threeRef = useRef(null);
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const videoRef = useRef(null);
  const pulseRef = useRef(0);
  const sharedMouse = useRef({ x: -999, y: -999 });

  // ═══ THREE.JS — Agents + Rings + Particles ═══
  useEffect(() => {
    const container = threeRef.current;
    if (!container) return;

    const w = container.clientWidth, h = container.clientHeight;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, w / h, 0.1, 100);
    camera.position.set(0, 0, 5.5);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    const loader = new THREE.TextureLoader();
    const clock = new THREE.Clock();

    scene.add(new THREE.AmbientLight(0x506080, 1.5));

    // ── Energy rings ──
    const ringMat1 = new THREE.MeshBasicMaterial({ color: 0x00D4FF, transparent: true, opacity: 0.18, depthWrite: false });
    const ring1 = new THREE.Mesh(new THREE.TorusGeometry(1.65, 0.004, 16, 128), ringMat1);
    ring1.rotation.x = Math.PI * 0.5;
    scene.add(ring1);
    const ringMat2 = new THREE.MeshBasicMaterial({ color: 0x007AFF, transparent: true, opacity: 0.08, depthWrite: false });
    const ring2 = new THREE.Mesh(new THREE.TorusGeometry(1.85, 0.003, 16, 128), ringMat2);
    ring2.rotation.x = Math.PI * 0.5;
    scene.add(ring2);

    // ── Ambient particles ──
    const pN = 28;
    const pPos = new Float32Array(pN * 3);
    const pCol = new Float32Array(pN * 3);
    const pSpd = [];
    for (let i = 0; i < pN; i++) {
      const th = Math.random() * Math.PI * 2;
      const r = 1.5 + Math.random() * 1.0;
      pPos[i * 3] = Math.cos(th) * r;
      pPos[i * 3 + 1] = (Math.random() - 0.5) * 1.8;
      pPos[i * 3 + 2] = Math.sin(th) * r * 0.15;
      const c = new THREE.Color([0x007AFF, 0x34C759, 0xFFAD00][i % 3]);
      pCol[i * 3] = c.r; pCol[i * 3 + 1] = c.g; pCol[i * 3 + 2] = c.b;
      pSpd.push(0.001 + Math.random() * 0.002);
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pPos, 3));
    pGeo.setAttribute('color', new THREE.BufferAttribute(pCol, 3));
    const pts = new THREE.Points(pGeo, new THREE.PointsMaterial({
      size: 0.018, vertexColors: true, transparent: true, opacity: 0.35,
      depthWrite: false, sizeAttenuation: true,
    }));
    scene.add(pts);

    // ── AI Agents (positionnés en haut à droite) ──
    const agentGroups = [];
    const orbitCenter = { x: 1.8, y: 1.2 };
    const orbitData = AI_AGENTS.map((_, i) => ({
      angle: (i / AI_AGENTS.length) * Math.PI * 2,
      speed: 0.0008 + Math.random() * 0.0004,
      rX: 0.9 + Math.random() * 0.15,
      rY: 0.55 + Math.random() * 0.08,
      fp: Math.random() * Math.PI * 2,
    }));

    AI_AGENTS.forEach((agent) => {
      const group = new THREE.Group();
      const colorHex = '#' + agent.color.toString(16).padStart(6, '0');

      const glowSprite = new THREE.Sprite(new THREE.SpriteMaterial({
        map: createGlowTexture(256, agent.color),
        transparent: true, opacity: 0.6, depthWrite: false,
        blending: THREE.AdditiveBlending,
      }));
      glowSprite.scale.set(1.5, 1.5, 1);
      group.add(glowSprite);

      loader.load(agent.img, (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
          map: tex, transparent: true, depthWrite: false,
        }));
        sprite.scale.set(0.68, 0.68, 1);
        group.add(sprite);
      });

      const label = new THREE.Sprite(new THREE.SpriteMaterial({
        map: createLabelTexture(agent.name, agent.title, colorHex),
        transparent: true, opacity: 0.85, depthWrite: false,
      }));
      label.scale.set(1.2, 0.32, 1);
      label.position.y = -0.5;
      group.add(label);

      scene.add(group);
      agentGroups.push({ group, glow: glowSprite });
    });

    // Connection lines
    const lineMats = AI_AGENTS.map(a =>
      new THREE.LineBasicMaterial({ color: a.color, transparent: true, opacity: 0.1, depthWrite: false })
    );
    const lines = AI_AGENTS.map((_, i) => {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(6), 3));
      const l = new THREE.Line(g, lineMats[i]);
      scene.add(l);
      return l;
    });

    // ═══ Animation ═══
    const animate = () => {
      const t = clock.getElapsedTime();
      pulseRef.current = Math.sin(t * 1.0) * 0.5 + 0.5;

      // Rings
      ring1.rotation.z = t * 0.15;
      ring1.rotation.x = Math.PI * 0.5 + Math.sin(t * 0.25) * 0.04;
      ringMat1.opacity = 0.14 + pulseRef.current * 0.06;
      ring2.rotation.z = -t * 0.1;
      ringMat2.opacity = 0.06 + pulseRef.current * 0.03;

      // Particles
      const pp = pts.geometry.attributes.position.array;
      for (let i = 0; i < pN; i++) {
        pp[i * 3 + 1] += pSpd[i];
        if (pp[i * 3 + 1] > 1.0) pp[i * 3 + 1] = -1.0;
      }
      pts.geometry.attributes.position.needsUpdate = true;
      pts.rotation.y = t * 0.02;

      // Agents (orbite en haut à droite)
      for (let i = 0; i < AI_AGENTS.length; i++) {
        const od = orbitData[i], ag = agentGroups[i];
        od.angle += od.speed; od.fp += 0.012;
        const ax = orbitCenter.x + Math.cos(od.angle) * od.rX;
        const ay = orbitCenter.y + Math.sin(od.angle) * od.rY + Math.sin(od.fp) * 0.03;
        const az = Math.sin(od.angle) * 0.15;
        ag.group.position.set(ax, ay, az);
        ag.group.lookAt(camera.position);
        const gs = 1.0 + pulseRef.current * 0.15;
        ag.glow.scale.set(gs, gs, 1);
        ag.glow.material.opacity = 0.3 + pulseRef.current * 0.15;
        const lp = lines[i].geometry.attributes.position.array;
        lp[0] = orbitCenter.x; lp[1] = orbitCenter.y; lp[2] = 0;
        lp[3] = ax; lp[4] = ay; lp[5] = az;
        lines[i].geometry.attributes.position.needsUpdate = true;
        lineMats[i].opacity = 0.04 + pulseRef.current * 0.04;
      }

      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    };
    animate();

    const onResize = () => {
      const nw = container.clientWidth, nh = container.clientHeight;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, []);

  // ═══ 2D CANVAS — Background neural network ═══
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let width, height;
    const nodes = [], pkts = [], N = 40;

    const resize = () => {
      const r = canvas.parentElement.getBoundingClientRect();
      width = canvas.width = r.width; height = canvas.height = r.height;
    };
    const initNodes = () => {
      nodes.length = 0; pkts.length = 0;
      const cx = width / 2, cy = height / 2;
      for (let i = 0; i < N; i++) {
        let x, y, a = 0;
        do { x = Math.random() * width; y = Math.random() * height; a++; }
        while (Math.abs(x - cx) < 150 && Math.abs(y - cy) < 110 && a < 20);
        nodes.push({
          x, y, vx: (Math.random() - 0.5) * 0.2, vy: (Math.random() - 0.5) * 0.2,
          r: Math.random() * 1.2 + 0.7, p: Math.random() * Math.PI * 2,
          c: ['#007AFF', '#34C759', '#FFAD00'][Math.floor(Math.random() * 3)],
        });
      }
    };
    const animateCanvas = () => {
      ctx.clearRect(0, 0, width, height);
      const sp = pulseRef.current, M = sharedMouse.current;
      for (const n of nodes) {
        n.x += n.vx; n.y += n.vy; n.p += 0.01;
        if (n.x < 0 || n.x > width) n.vx *= -1;
        if (n.y < 0 || n.y > height) n.vy *= -1;
        if (M.x > 0) {
          const dx = n.x - M.x, dy = n.y - M.y, d = Math.sqrt(dx * dx + dy * dy);
          if (d < 80) { n.vx += (dx / d) * 0.06; n.vy += (dy / d) * 0.06; }
        }
        n.vx *= 0.998; n.vy *= 0.998;
      }
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 140) {
            ctx.beginPath(); ctx.moveTo(nodes[i].x, nodes[i].y); ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(0,122,255,${(1 - dist / 140) * (0.06 + sp * 0.05)})`;
            ctx.lineWidth = 0.5; ctx.stroke();
            if (Math.random() < 0.0004) pkts.push({ fi: i, ti: j, t: 0, s: 0.01 + Math.random() * 0.012 });
          }
        }
      }
      for (let c = pkts.length - 1; c >= 0; c--) {
        const p = pkts[c]; p.t += p.s;
        if (p.t > 1) { pkts.splice(c, 1); continue; }
        const a = nodes[p.fi], b = nodes[p.ti];
        ctx.beginPath();
        ctx.arc(a.x + (b.x - a.x) * p.t, a.y + (b.y - a.y) * p.t, 1.2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(52,199,89,${0.5 - p.t * 0.35})`; ctx.fill();
      }
      for (const n of nodes) {
        const g = Math.sin(n.p) * 0.25 + 0.75;
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r * g, 0, Math.PI * 2);
        ctx.fillStyle = n.c + Math.round(g * 50).toString(16).padStart(2, '0'); ctx.fill();
      }
      animRef.current = requestAnimationFrame(animateCanvas);
    };
    resize(); initNodes(); animateCanvas();
    const onR = () => { resize(); initNodes(); };
    window.addEventListener('resize', onR);
    return () => { cancelAnimationFrame(animRef.current); window.removeEventListener('resize', onR); };
  }, []);

  const ActiveComponent = SLIDES[0].component;

  return (
    <div ref={containerRef} className="relative w-full flex items-center justify-center overflow-hidden rounded-2xl" style={{ height: '80vh', minHeight: '600px', backgroundColor: '#0B1120' }}>

      {/* Layer 0: Video + fallback */}
      <video ref={videoRef} autoPlay loop muted playsInline
        className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-1000"
        style={{ zIndex: 0 }}
        onCanPlay={(e) => { e.target.style.opacity = '0.2'; }}>
        <source src="/neural_bg.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{ backgroundImage: `url(${BG_IMAGE})`, zIndex: 0 }} />

      {/* Layer 1: Vignette */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at center, transparent 10%, #0B112030 35%, #0B1120CC 60%, #0B1120 78%)',
        zIndex: 1,
      }} />

      {/* Layer 2: 2D Canvas neural network */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ zIndex: 2, pointerEvents: 'none' }} />

      {/* Layer 3: Three.js agents + rings + particles */}
      <div ref={threeRef} className="absolute inset-0" style={{ zIndex: 3, pointerEvents: 'none' }} />

      {/* Layer 4: Full-screen 3D view */}
      <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 4 }} data-testid="home-logo">
        {/* Slide title */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10">
          <div className="px-4 py-1.5 rounded-full bg-slate-900/70 backdrop-blur-md border border-slate-700/50" data-testid="slide-title">
            <span className="text-sm font-medium text-slate-300">{SLIDES[0].label}</span>
          </div>
        </div>

        {/* Slide — fills full hero */}
        <div className="absolute inset-0 overflow-hidden">
          <Suspense fallback={<SlideLoader />}>
            <ActiveComponent embedded />
          </Suspense>
        </div>
      </div>
    </div>
  );
}
